import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, Upload, Trash2, Download, CheckCircle2, Building2, Landmark, FileText,
  HardHat, Wrench, ClipboardCheck, ArrowLeft, ArrowRight, Share2, AlertTriangle,
} from "lucide-react";

const pub = axios.create({ baseURL: API_BASE });

const DOC_ORDER = ["insurance_pl", "insurance_wc", "licences", "quality_cert", "agreement"];
const DOC_META_KINDS = ["insurance_pl", "insurance_wc", "licences", "quality_cert"];
const INSURER_KINDS = ["insurance_pl", "insurance_wc"];
const WHS_QS = [
  ["policy", "Do you have a written WHS policy?"],
  ["swms", "Do you provide SWMS (safe work method statements) for site work?"],
  ["incidents", "Do you have an incident reporting & investigation procedure?"],
  ["training", "Are your staff trained & licensed for the work they perform?"],
];
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Shell = ({ data, children }) => (
  <div className="min-h-screen bg-slate-100 py-6 px-3 sm:py-10" data-testid="supplier-onboarding-page">
    <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-sm shadow-[4px_4px_0_rgba(15,23,42,.06)] overflow-hidden">
      <div className="bg-slate-900 px-5 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center text-white font-extrabold text-xs shrink-0">CC</div>
        <div>
          <div className="text-white font-bold text-sm">CCFI Supplier Onboarding</div>
          <div className="text-slate-400 text-[10.5px]">
            {data ? <>{data.supplier_name} · link valid until {fmtD(data.expires_at)}</> : "Central Coast Fencing Industries"}
          </div>
        </div>
      </div>
      {children}
    </div>
    <p className="text-center text-[10px] text-slate-400 mt-4">Central Coast Fencing Industries — approved supplier register</p>
  </div>
);

const Lbl = ({ children, req }) => (
  <div className="text-[9.5px] font-bold tracking-widest uppercase text-slate-500 mb-1">{children}{req && <span className="text-rose-600"> *</span>}</div>
);

function UploadZone({ kind, label, req, uploads, token, onChange }) {
  const [busy, setBusy] = useState(false);
  const mine = uploads.filter((u) => u.kind === kind);
  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const r = await pub.post(`/public/supplier-onboarding/${token}/upload`, fd);
      onChange([...uploads, r.data]);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — PDF, photo or Word doc up to 15 MB");
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try {
      await pub.delete(`/public/supplier-onboarding/${token}/upload/${id}`);
      onChange(uploads.filter((u) => u.id !== id));
    } catch { toast.error("Could not remove the file"); }
  };
  return (
    <div className="mb-3">
      <Lbl req={req}>{label}</Lbl>
      {mine.map((u) => (
        <div key={u.id} className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2 text-xs mb-1.5" data-testid={`upload-done-${kind}`}>
          <span className="flex items-center gap-1.5 truncate text-slate-700"><FileText size={12} className="text-emerald-600 shrink-0" />{u.name}</span>
          <span className="flex items-center gap-2 shrink-0">
            <span className="text-emerald-600 font-bold text-[10px]">✓ Uploaded</span>
            <button type="button" onClick={() => remove(u.id)} className="text-slate-400 hover:text-rose-600" data-testid={`upload-remove-${u.id}`}><Trash2 size={12} /></button>
          </span>
        </div>
      ))}
      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-sm p-4 text-xs text-slate-500 cursor-pointer hover:border-cyan-500 hover:bg-cyan-50/40 bg-slate-50" data-testid={`upload-zone-${kind}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {mine.length ? "Upload another file" : "Tap to upload a PDF, photo or Word doc"}
        <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
    </div>
  );
}

export default function SupplierOnboarding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("intro"); // intro | 0..n | done
  const [draft, setDraft] = useState({});
  const [uploads, setUploads] = useState([]);
  const [declName, setDeclName] = useState("");
  const [declTick, setDeclTick] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    pub.get(`/public/supplier-onboarding/${token}`)
      .then((r) => {
        setData(r.data);
        setDraft(r.data.draft || {});
        setUploads(r.data.uploads || []);
      })
      .catch((e) => setErr({ code: e?.response?.status, msg: e?.response?.data?.detail || "This onboarding link is invalid" }))
      .finally(() => setLoading(false));
  }, [token]);

  const S = data?.sections || {};
  const defs = useMemo(() => Object.fromEntries((data?.section_defs || []).map((d) => [d.key, d])), [data]);
  const req = (k) => S[k] === "required";

  const steps = useMemo(() => {
    if (!data) return [];
    const st = [{ key: "company", title: "Company & contacts", icon: Building2 }];
    // Only sections marked "required" when the link was sent appear on the form.
    if (S.banking === "required") st.push({ key: "banking", title: "Bank details", icon: Landmark });
    const docKinds = DOC_ORDER.filter((k) => S[k] === "required");
    if (docKinds.length) st.push({ key: "docs", title: "Documents & certifications", icon: FileText, docKinds });
    if (S.whs === "required") st.push({ key: "whs", title: "WHS questionnaire", icon: HardHat });
    if (S.capability === "required") st.push({ key: "capability", title: "Capability & references", icon: Wrench });
    st.push({ key: "review", title: "Review & submit", icon: ClipboardCheck });
    return st;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = useCallback((d) => {
    pub.put(`/public/supplier-onboarding/${token}/draft`, { draft: d }).catch(() => {});
  }, [token]);

  const set = (path, val) => {
    setDraft((d) => {
      const nd = { ...d, [path[0]]: { ...(d[path[0]] || {}) } };
      if (path.length === 2) nd[path[0]][path[1]] = val;
      else nd[path[0]] = { ...(nd[path[0]] || {}), [path[1]]: { ...((d[path[0]] || {})[path[1]] || {}), [path[2]]: val } };
      return nd;
    });
  };

  const goto = (idx) => { saveDraft(draft); setStage(idx); window.scrollTo(0, 0); };

  // Debounced autosave — the footer promises progress saves automatically.
  useEffect(() => {
    if (typeof stage !== "number") return undefined;
    const t = setTimeout(() => saveDraft(draft), 1200);
    return () => clearTimeout(t);
  }, [draft, stage, saveDraft]);

  const downloadAgreement = async () => {
    try {
      const r = await pub.get(`/public/supplier-onboarding/${token}/agreement`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = data?.agreement?.name || "agreement.pdf"; a.click();
    } catch { toast.error("Could not download the agreement"); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await pub.post(`/public/supplier-onboarding/${token}/submit`, { draft, declaration_name: declName.trim() });
      setStage("done");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please check the required sections");
    } finally { setSubmitting(false); }
  };

  const requiredChecklist = useMemo(() => {
    if (!data) return [];
    const order = ["company", "banking", ...DOC_ORDER, "whs", "capability"];
    return order.filter((k) => S[k] === "required" || k === "company").map((k) => defs[k]?.label || k);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: Shell lives at module scope — defining it inside this component re-created
  // it on every keystroke, remounting the form and dropping input focus (bug fix).

  if (loading) return <Shell data={data}><div className="p-10 flex justify-center"><Loader2 size={22} className="animate-spin text-cyan-600" /></div></Shell>;

  if (err) return (
    <Shell data={data}>
      <div className="p-8 text-center" data-testid="onboarding-error">
        <AlertTriangle size={30} className="mx-auto text-amber-500 mb-3" />
        <div className="font-bold text-slate-900 text-lg">{err.code === 410 ? "This link is no longer open" : "Link not found"}</div>
        <p className="text-sm text-slate-500 mt-2">{err.msg}</p>
        <p className="text-xs text-slate-400 mt-4">Need a fresh link? Reply to the email you received or call Central Coast Fencing Industries.</p>
      </div>
    </Shell>
  );

  if (stage === "done") return (
    <Shell data={data}>
      <div className="p-8 text-center" data-testid="onboarding-success">
        <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
        <div className="font-bold text-slate-900 text-lg">Thanks — submission received</div>
        <p className="text-sm text-slate-500 mt-2">Your details and documents are with the CCFI team for review. We'll email you once you're approved, or if anything else is needed.</p>
      </div>
    </Shell>
  );

  if (stage === "intro") return (
    <Shell data={data}>
      <div className="p-5 sm:p-6">
        {data.changes_note && (
          <div className="border border-rose-200 bg-rose-50 rounded-sm px-3 py-2.5 text-xs text-rose-700 mb-4" data-testid="changes-note-banner">
            <b>CCFI asked for a few updates:</b> “{data.changes_note}” — your previous answers are saved below.
          </div>
        )}
        <div className="font-bold text-slate-900 text-lg">Welcome — before you start</div>
        <p className="text-xs text-slate-500 mt-1.5 mb-4">
          Central Coast Fencing Industries is updating its approved supplier register. This takes about 10–15 minutes.
          {" "}<b>Have these ready before you begin:</b>
        </p>
        <div className="space-y-1.5 mb-4" data-testid="intro-checklist">
          {requiredChecklist.map((l) => (
            <div key={l} className="flex items-center gap-2 border border-slate-200 bg-slate-50 rounded-sm px-3 py-2 text-xs text-slate-700">
              <CheckCircle2 size={13} className="text-cyan-600 shrink-0" /> {l}
            </div>
          ))}
        </div>
        {data.message && <div className="border border-cyan-200 bg-cyan-50 rounded-sm px-3 py-2.5 text-xs text-slate-700 italic mb-4">“{data.message}” — {data.contact_name ? `message from CCFI` : "CCFI"}</div>}
        <div className="flex items-start gap-2 border border-slate-200 rounded-sm px-3 py-2.5 text-[11px] text-slate-500 bg-slate-50 mb-5">
          <Share2 size={13} className="shrink-0 mt-0.5 text-slate-400" />
          <span>Not the right person? <b>Forward this link</b> to a colleague — anyone in your organisation can complete it, and progress saves as you go. It locks once submitted.</span>
        </div>
        <Button onClick={() => goto(0)} className="w-full rounded-sm bg-cyan-600 hover:bg-cyan-700 h-11 font-bold" data-testid="start-onboarding-btn">
          Start <ArrowRight size={15} className="ml-2" />
        </Button>
      </div>
    </Shell>
  );

  const idx = stage;
  const step = steps[idx];
  const comp = draft.company || {};
  const bank = draft.banking || {};
  const whs = draft.whs || {};
  const cap = draft.capability || {};
  const contacts = comp.contacts || [{}];

  return (
    <Shell data={data}>
      <div className="p-5 sm:p-6">
        <div className="flex gap-1 mb-2">
          {steps.map((s, i) => <span key={s.key} className={`flex-1 h-1 rounded-full ${i <= idx ? "bg-cyan-600" : "bg-slate-200"}`} />)}
        </div>
        <div className="text-[10px] text-slate-400 mb-4" data-testid="step-indicator">Step {idx + 1} of {steps.length} — {step.title}</div>

        {step.key === "company" && (
          <div data-testid="step-company">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><Lbl req>ABN</Lbl><Input data-testid="company-abn" value={comp.abn || ""} onChange={(e) => set(["company", "abn"], e.target.value)} placeholder="11 222 333 444" className="rounded-sm h-10" /></div>
              <div><Lbl>Website</Lbl><Input data-testid="company-website" value={comp.website || ""} onChange={(e) => set(["company", "website"], e.target.value)} placeholder="www…" className="rounded-sm h-10" /></div>
            </div>
            <div className="mb-3"><Lbl>Registered address</Lbl><Input data-testid="company-address" value={comp.address ?? data.address ?? ""} onChange={(e) => set(["company", "address"], e.target.value)} className="rounded-sm h-10" /></div>
            <Lbl req>Key contacts</Lbl>
            {contacts.map((c, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3 mb-2 grid grid-cols-2 gap-2">
                <Input data-testid={`contact-name-${i}`} value={c.name || ""} onChange={(e) => { const cs = [...contacts]; cs[i] = { ...c, name: e.target.value }; set(["company", "contacts"], cs); }} placeholder="Name" className="rounded-sm h-9 text-sm" />
                <Input value={c.role || ""} onChange={(e) => { const cs = [...contacts]; cs[i] = { ...c, role: e.target.value }; set(["company", "contacts"], cs); }} placeholder="Role (e.g. Sales / Accounts)" className="rounded-sm h-9 text-sm" />
                <Input value={c.email || ""} onChange={(e) => { const cs = [...contacts]; cs[i] = { ...c, email: e.target.value }; set(["company", "contacts"], cs); }} placeholder="Email" className="rounded-sm h-9 text-sm" />
                <Input value={c.phone || ""} onChange={(e) => { const cs = [...contacts]; cs[i] = { ...c, phone: e.target.value }; set(["company", "contacts"], cs); }} placeholder="Phone" className="rounded-sm h-9 text-sm" />
              </div>
            ))}
            {contacts.length < 3 && (
              <button type="button" data-testid="add-contact-btn" onClick={() => set(["company", "contacts"], [...contacts, {}])} className="text-[11px] font-semibold text-cyan-700 hover:underline">+ Add another contact</button>
            )}
          </div>
        )}

        {step.key === "banking" && (
          <div data-testid="step-banking">
            <p className="text-[11px] text-slate-500 mb-3">Bank details are stored securely and only visible to CCFI admins &amp; supplier approvers.</p>
            <div className="mb-3"><Lbl req={req("banking")}>Account name</Lbl><Input data-testid="bank-account-name" value={bank.account_name || ""} onChange={(e) => set(["banking", "account_name"], e.target.value)} className="rounded-sm h-10" /></div>
            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <div><Lbl req={req("banking")}>BSB</Lbl><Input data-testid="bank-bsb" value={bank.bsb || ""} onChange={(e) => set(["banking", "bsb"], e.target.value)} placeholder="032-583" className="rounded-sm h-10" /></div>
              <div><Lbl req={req("banking")}>Account number</Lbl><Input data-testid="bank-account-number" value={bank.account_number || ""} onChange={(e) => set(["banking", "account_number"], e.target.value)} className="rounded-sm h-10" /></div>
            </div>
          </div>
        )}

        {step.key === "docs" && (
          <div data-testid="step-docs">
            {step.docKinds.map((k) => (
              <div key={k} className="mb-5">
                {k === "agreement" && data.agreement && (
                  <button type="button" onClick={downloadAgreement} data-testid="download-agreement-btn"
                    className="w-full flex items-center justify-between border border-cyan-200 bg-cyan-50 rounded-sm px-3 py-2.5 text-xs text-slate-700 mb-2 hover:bg-cyan-100">
                    <span className="flex items-center gap-1.5 truncate"><FileText size={13} className="text-cyan-700" />{data.agreement.name}</span>
                    <span className="flex items-center gap-1 text-cyan-700 font-bold shrink-0"><Download size={12} /> Download, sign &amp; upload below</span>
                  </button>
                )}
                <UploadZone kind={k} label={defs[k]?.label || k} req={req(k)} uploads={uploads} token={token} onChange={setUploads} />
                {DOC_META_KINDS.includes(k) && uploads.some((u) => u.kind === k) && (
                  <div className="grid grid-cols-2 gap-3 -mt-1">
                    {INSURER_KINDS.includes(k) && (
                      <div><Lbl>Insurer</Lbl><Input data-testid={`insurer-${k}`} value={draft.doc_meta?.[k]?.insurer || ""} onChange={(e) => set(["doc_meta", k, "insurer"], e.target.value)} placeholder="e.g. QBE" className="rounded-sm h-9 text-sm" /></div>
                    )}
                    <div className={INSURER_KINDS.includes(k) ? "" : "col-span-2"}>
                      <Lbl req>Expiry date</Lbl>
                      <Input data-testid={`expiry-${k}`} type="date" value={draft.doc_meta?.[k]?.expiry || ""} onChange={(e) => set(["doc_meta", k, "expiry"], e.target.value)} className="rounded-sm h-9 text-sm" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {step.key === "whs" && (
          <div data-testid="step-whs">
            {WHS_QS.map(([k, q]) => (
              <div key={k} className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2.5 mb-2 text-xs text-slate-700">
                <span className="flex-1 pr-3">{q}</span>
                <div className="flex gap-1 shrink-0">
                  {["yes", "no"].map((v) => (
                    <button key={v} type="button" data-testid={`whs-${k}-${v}`} onClick={() => set(["whs", k], v)}
                      className={`px-3 py-1 rounded-sm text-[10px] font-bold border capitalize ${whs[k] === v ? (v === "yes" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-rose-600 border-rose-600 text-white") : "bg-white border-slate-300 text-slate-500"}`}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
            <Lbl>Anything else about your WHS systems (optional)</Lbl>
            <Textarea data-testid="whs-notes" rows={2} value={whs.notes || ""} onChange={(e) => set(["whs", "notes"], e.target.value)} className="rounded-sm text-sm" />
          </div>
        )}

        {step.key === "capability" && (
          <div data-testid="step-capability">
            <div className="mb-3"><Lbl req={req("capability")}>Main products / services you supply</Lbl><Textarea data-testid="cap-products" rows={2} value={cap.products || ""} onChange={(e) => set(["capability", "products"], e.target.value)} className="rounded-sm text-sm" /></div>
            <div className="mb-3"><Lbl>Typical lead times</Lbl><Input data-testid="cap-leadtimes" value={cap.lead_times || ""} onChange={(e) => set(["capability", "lead_times"], e.target.value)} placeholder="e.g. Colorbond sheets 3–5 business days" className="rounded-sm h-10" /></div>
            <div><Lbl>Trade references (optional)</Lbl><Textarea data-testid="cap-references" rows={2} value={cap.references || ""} onChange={(e) => set(["capability", "references"], e.target.value)} placeholder="Two businesses we can contact" className="rounded-sm text-sm" /></div>
          </div>
        )}

        {step.key === "review" && (
          <div data-testid="step-review">
            <div className="space-y-1.5 mb-4 text-xs text-slate-700">
              <div className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2">🏢 <b>{data.supplier_name}</b>{comp.abn ? ` · ABN ${comp.abn}` : ""} · {(comp.contacts || []).filter((c) => c.name).length || 0} contact(s)</div>
              {S.banking && <div className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2">🏦 Bank details {bank.bsb && bank.account_number ? "provided" : <span className="text-rose-600 font-semibold">missing</span>}</div>}
              {DOC_ORDER.filter((k) => S[k]).map((k) => {
                const n = uploads.filter((u) => u.kind === k).length;
                return <div key={k} className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2">📄 {defs[k]?.label} — {n ? `${n} file(s) uploaded` : req(k) ? <span className="text-rose-600 font-semibold">required, not uploaded</span> : "not uploaded (optional)"}</div>;
              })}
            </div>
            <Lbl req>Your full name (declaration)</Lbl>
            <Input data-testid="declaration-name" value={declName} onChange={(e) => setDeclName(e.target.value)} placeholder="Full name" className="rounded-sm h-10 mb-2" />
            <label className="flex items-start gap-2 border border-slate-200 rounded-sm px-3 py-2.5 text-[11px] text-slate-600 cursor-pointer mb-4">
              <input type="checkbox" checked={declTick} onChange={(e) => setDeclTick(e.target.checked)} className="mt-0.5" data-testid="declaration-tick" />
              <span>I declare the information provided is true and correct, and agree to notify Central Coast Fencing Industries of any changes to insurances, licences or certifications.</span>
            </label>
            <Button data-testid="submit-onboarding-btn" disabled={submitting || !declName.trim() || !declTick} onClick={submit}
              className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 h-11 font-bold">
              {submitting ? <Loader2 size={15} className="animate-spin mr-2" /> : <CheckCircle2 size={15} className="mr-2" />} Submit to CCFI
            </Button>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <Button variant="outline" className="rounded-sm" data-testid="step-back-btn"
            onClick={() => (idx === 0 ? (saveDraft(draft), setStage("intro")) : goto(idx - 1))}>
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Button>
          {step.key !== "review" && (
            <Button className="rounded-sm bg-cyan-600 hover:bg-cyan-700" data-testid="step-next-btn" onClick={() => goto(idx + 1)}>
              Continue <ArrowRight size={14} className="ml-1.5" />
            </Button>
          )}
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-4">Progress saves automatically — you can close this page and come back with the same link.</p>
      </div>
    </Shell>
  );
}
